import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import {
  MachineReservationFormDialogComponent,
  MachineReservationDialogData,
} from 'src/app/machine/machine-reservation-form-dialog/machine-reservation-form-dialog.component';

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  reservations: any[];
}

@Component({
  selector: 'app-project-machine-availability',
  templateUrl: './project-machine-availability.component.html',
  styleUrls: ['./project-machine-availability.component.css'],
})
export class ProjectMachineAvailabilityComponent implements OnInit, OnChanges {
  @Input() projectId!: number | string;

  loading = false;
  reservations: any[] = [];
  calendarDays: CalendarDay[] = [];
  calendarMonth = new Date();
  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  constructor(
    private api: ApiService,
    private toastr: ToastrMsgService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.tryLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId']) {
      this.tryLoad();
    }
  }

  private tryLoad(): void {
    const id = this.projectId == null ? null : Number(this.projectId);
    if (id == null || isNaN(id)) {
      this.loading = false;
      return;
    }
    this.load(id);
  }

  load(projectId: number): void {
    this.loading = true;
    const range = this.calendarRange;
    this.api.getMachineReservationCalendar({
      from_date: range.from,
      to_date: range.to,
      project_id: projectId,
    }).subscribe({
      next: (res: any) => {
        this.reservations = res?.data || [];
        this.buildCalendar();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.message || 'Failed to load machine reservations');
      },
    });
  }

  get calendarRange(): { from: string; to: string } {
    const year = this.calendarMonth.getFullYear();
    const month = this.calendarMonth.getMonth();
    const from = new Date(year, month, 1);
    const to = new Date(year, month + 1, 0);
    return {
      from: this.formatDate(from),
      to: this.formatDate(to),
    };
  }

  get calendarTitle(): string {
    return this.calendarMonth.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    });
  }

  prevMonth(): void {
    this.calendarMonth = new Date(
      this.calendarMonth.getFullYear(),
      this.calendarMonth.getMonth() - 1,
      1
    );
    this.tryLoad();
  }

  nextMonth(): void {
    this.calendarMonth = new Date(
      this.calendarMonth.getFullYear(),
      this.calendarMonth.getMonth() + 1,
      1
    );
    this.tryLoad();
  }

  private buildCalendar(): void {
    const year = this.calendarMonth.getFullYear();
    const month = this.calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: CalendarDay[] = [];
    for (let i = 0; i < startDay; i++) {
      const d = new Date(year, month, -startDay + i + 1);
      days.push({ date: d, inMonth: false, isToday: false, reservations: [] });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const key = this.formatDate(date);
      const dayRes = this.reservations.filter((r) => {
        const start = (r.start_date || '').slice(0, 10);
        const end = (r.end_date || '').slice(0, 10);
        return key >= start && key <= end;
      });
      days.push({
        date,
        inMonth: true,
        isToday: date.getTime() === today.getTime(),
        reservations: dayRes,
      });
    }
    while (days.length % 7 !== 0) {
      const last = days[days.length - 1].date;
      const d = new Date(last);
      d.setDate(d.getDate() + 1);
      days.push({ date: d, inMonth: false, isToday: false, reservations: [] });
    }
    this.calendarDays = days;
  }

  reservationBarClass(status: string): string {
    const map: Record<string, string> = {
      pending: 'bg-amber-500',
      confirmed: 'bg-blue-600',
      in_use: 'bg-emerald-600',
      completed: 'bg-gray-500',
      cancelled: 'bg-red-400 line-through',
    };
    return map[status] || 'bg-slate-500';
  }

  openBookDialog(reservation?: any): void {
    const pid = Number(this.projectId);
    const data: MachineReservationDialogData = {
      reservation,
      prefillProjectId: !isNaN(pid) ? pid : undefined,
    };
    this.dialog
      .open(MachineReservationFormDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        autoFocus: 'first-tabbable',
        panelClass: 'machine-reservation-dialog',
        data,
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.tryLoad();
      });
  }

  openFullCalendar(): void {
    const pid = Number(this.projectId);
    void this.router.navigate(['/home/machine-reservations'], {
      queryParams: !isNaN(pid) ? { project_id: pid } : {},
    });
  }

  get upcomingCount(): number {
    const today = this.formatDate(new Date());
    return this.reservations.filter(
      (r) => (r.end_date || '').slice(0, 10) >= today && r.status !== 'cancelled'
    ).length;
  }

  private formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
